// The auroral oval's shape for the viewer's aurora shader
// (earthAuroraFragmentShader in src/shaders/aurora.js), as a short Fourier
// series round magnetic local time per hemisphere:
//
//   lat0(tau)   the main arc's magnetic latitude (dipole), degrees
//   sector(tau) how bright the arcs are there, relative (the model's
//               0.12 + 0.88 * midnight factor)
//
// tau is the shader's angle from magnetic noon (atan(sT, cT) in auPoint).
// Four harmonics are enough for the oval's offset, its noon-midnight
// asymmetry and bright sectors of about three hours, and keep the
// shape smooth, which the march relies on (its step bound assumes the
// sheet field changes slowly along the oval).
//
// The model's own shape is lat0 = 72 + 3.2 cos(tau) and sector = 0.56 -
// 0.44 cos(tau), so a series with only those terms draws exactly the
// approved look. Kp alone moves it equatorward 2 degrees per Kp (NOAA's
// rule, geomag.js); the OVATION nowcast replaces it with the real shape.

import { DIPOLE_POLE, sunPosition } from './geomag.js';

export const HARMONICS = 4;
const N = 2 * HARMONICS + 1;
const DEG = Math.PI / 180;
// The Kp the approved look stands for: a quiet-to-moderate night.
export const MODEL_KP = 2;

// The model's coefficients: [a0, a1, b1, a2, b2, ...] for lat and sector.
export function modelOval(kp = MODEL_KP) {
  const lat = new Array(N).fill(0);
  const sector = new Array(N).fill(0);
  lat[0] = 72 - 2 * (kp - MODEL_KP);
  lat[1] = 3.2;
  sector[0] = 0.56;
  sector[1] = -0.44;
  return { lat, sector };
}

// Evaluate a series at tau.
export function evalSeries(c, tau) {
  let v = c[0];
  for (let k = 1; k <= HARMONICS; k++) v += c[2 * k - 1] * Math.cos(k * tau) + c[2 * k] * Math.sin(k * tau);
  return v;
}

// The shader's frame, in the Earth mesh's axes (earthMeshDirection in
// main.js): x lon 0, y north, -z lon 90 east.
function meshDir(lat, lon) {
  const a = lat * DEG;
  const b = lon * DEG;
  return [Math.cos(a) * Math.cos(b), Math.sin(a), -Math.cos(a) * Math.sin(b)];
}
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export function magneticFrame(pole = DIPOLE_POLE) {
  const axis = meshDir(pole.lat, pole.lon);
  // e1 = (0, 1, 0) x axis, normalised; e2 = axis x e1 (as main.js).
  let e1 = [axis[2], 0, -axis[0]];
  const l = Math.hypot(e1[0], e1[2]);
  e1 = [e1[0] / l, 0, e1[2] / l];
  const e2 = [axis[1] * e1[2] - axis[2] * e1[1], axis[2] * e1[0] - axis[0] * e1[2], axis[0] * e1[1] - axis[1] * e1[0]];
  return { axis, e1, e2 };
}

// Magnetic latitude (dipole, signed) and tau for a place with the Sun's
// sub-solar point given.
export function magCoords(lat, lon, sub, f = magneticFrame()) {
  const n = meshDir(lat, lon);
  const s = meshDir(sub.lat, sub.lon);
  const mlat = Math.asin(Math.max(-1, Math.min(1, dot(n, f.axis)))) / DEG;
  const mx = dot(n, f.e1);
  const my = dot(n, f.e2);
  let sx = dot(s, f.e1);
  let sy = dot(s, f.e2);
  const sl = Math.hypot(sx, sy) || 1;
  sx /= sl;
  sy /= sl;
  const cT = mx * sx + my * sy;
  const sT = my * sx - mx * sy;
  return { mlat, tau: Math.atan2(sT, cT) };
}

const BINS = 96;

// OVATION probabilities (parse.js parseOvation's grid: 181 x 360, lat -90..90
// by lon 0..359 east) -> the shape in the shader's frame, per hemisphere:
// { north: { lat, sector, power }, south: ... } or null for a hemisphere
// with nothing in it. The probabilities are binned by tau, weighted by cell
// area; each bin's latitude is the probability-weighted mean; the sector
// strength is the bin's share relative to the brightest bin of both
// hemispheres, with a floor so no stretch of the oval goes quite dark.
export function ovationOval(grid, atMs) {
  if (!grid || grid.length !== 360 * 181) return null;
  const sub = sunPosition(atMs);
  const f = magneticFrame();
  const acc = { north: { w: new Float64Array(BINS), m: new Float64Array(BINS) }, south: { w: new Float64Array(BINS), m: new Float64Array(BINS) } };
  for (let la = -89; la <= 89; la++) {
    if (Math.abs(la) < 35) continue;
    const area = Math.cos(la * DEG);
    for (let lo = 0; lo < 360; lo++) {
      const p = grid[(la + 90) * 360 + lo];
      if (p <= 0) continue;
      const { mlat, tau } = magCoords(la, lo, sub, f);
      if (Math.abs(mlat) < 40 || Math.abs(mlat) > 88) continue;
      const h = mlat >= 0 ? acc.north : acc.south;
      const bin = Math.min(BINS - 1, Math.floor(((tau / (2 * Math.PI)) + 0.5) * BINS));
      h.w[bin] += p * area;
      h.m[bin] += p * area * Math.abs(mlat);
    }
  }
  // Circular smoothing over about ten degrees of tau.
  const smooth = (arr, sigma = 1.5) => {
    const out = new Float64Array(BINS);
    const r = Math.ceil(3 * sigma);
    for (let i = 0; i < BINS; i++) {
      let s = 0;
      let ws = 0;
      for (let j = -r; j <= r; j++) {
        const k = Math.exp(-0.5 * (j / sigma) ** 2);
        s += k * arr[(i + j + BINS) % BINS];
        ws += k;
      }
      out[i] = s / ws;
    }
    return out;
  };
  let peak = 0;
  const pre = {};
  for (const hemi of ['north', 'south']) {
    const w = smooth(acc[hemi].w);
    const m = smooth(acc[hemi].m);
    pre[hemi] = { w, m };
    for (const v of w) peak = Math.max(peak, v);
  }
  if (peak <= 0) return null;
  const out = {};
  for (const hemi of ['north', 'south']) {
    const { w, m } = pre[hemi];
    const total = w.reduce((a, b) => a + b, 0);
    if (total < peak * 2) { out[hemi] = null; continue; }
    // Bins too faint to place: filled round the circle from the placed ones.
    const lat = new Array(BINS).fill(null);
    for (let i = 0; i < BINS; i++) if (w[i] > peak * 0.02) lat[i] = m[i] / w[i];
    const known = lat.map((v, i) => (v === null ? -1 : i)).filter((i) => i >= 0);
    if (known.length < 4) { out[hemi] = null; continue; }
    for (let i = 0; i < BINS; i++) {
      if (lat[i] !== null) continue;
      let a = i;
      let b = i;
      while (lat[(a + BINS) % BINS] === null) a--;
      while (lat[b % BINS] === null) b++;
      const la = lat[(a + BINS) % BINS];
      const lb = lat[b % BINS];
      lat[i] = la + ((lb - la) * (i - a)) / (b - a);
    }
    const sector = Array.from(w, (v) => 0.08 + 0.92 * (v / peak));
    const tauOf = (i) => -Math.PI + ((i + 0.5) * 2 * Math.PI) / BINS;
    const series = (vals) => {
      const c = new Array(N).fill(0);
      for (let i = 0; i < BINS; i++) {
        const t = tauOf(i);
        c[0] += vals[i] / BINS;
        for (let k = 1; k <= HARMONICS; k++) {
          c[2 * k - 1] += (2 / BINS) * vals[i] * Math.cos(k * t);
          c[2 * k] += (2 / BINS) * vals[i] * Math.sin(k * t);
        }
      }
      return c.map((v) => Math.round(v * 1000) / 1000);
    };
    const latC = series(lat);
    // Keep the shape gentle: the steepest the series may change is about
    // 6 degrees per radian of tau (a tenth of a km per km round the oval).
    let slope = 0;
    for (let k = 1; k <= HARMONICS; k++) slope += k * Math.hypot(latC[2 * k - 1], latC[2 * k]);
    if (slope > 6) for (let i = 1; i < N; i++) latC[i] = Math.round(latC[i] * (6 / slope) * 1000) / 1000;
    out[hemi] = { lat: latC, sector: series(sector), power: Math.round(total) };
  }
  return out;
}

// The uniforms the shader takes (uOval, vec4[10]): per hemisphere
// [a0 lat, a0 sector, 0, 0], [lat a1 b1 a2 b2], [lat a3 b3 a4 b4],
// [sector a1 b1 a2 b2], [sector a3 b3 a4 b4]; north first.
export function ovalUniformValues(north, south) {
  const pack = (o) => [
    [o.lat[0], o.sector[0], 0, 0],
    [o.lat[1], o.lat[2], o.lat[3], o.lat[4]],
    [o.lat[5], o.lat[6], o.lat[7], o.lat[8]],
    [o.sector[1], o.sector[2], o.sector[3], o.sector[4]],
    [o.sector[5], o.sector[6], o.sector[7], o.sector[8]],
  ];
  return [...pack(north), ...pack(south)];
}
